import type { ClipboardActions, InspectedDoc, TreeViewOutletContextValue } from "@/components/layout/TreeViewOutletContext";
import { TreeViewOutletContext, useTreeViewOutletContext } from "@/components/layout/TreeViewOutletContext";
import { SlideablePanel } from "@/components/ui/SlideablePanel";
import { ClipboardPreviewPanel } from "@/features/clipboard/components/ClipboardPreviewPanel";
import type { DocumentContent } from "@/features/clipboard/api/clipboard";
import { PaperDetailPanel } from "@/features/repos/components/PaperDetailPanel";
import type { RepoPaperRow } from "@/features/repos/api/paperRepos";
import useBreakpoints from "@/hooks/useBreakpoints";
import { closeLeftSidebarAtom, leftSidebarOpenAtom } from "@/utils/sidebarStore";
import { useAtom, useSetAtom } from "jotai";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Outlet, useLocation, useMatch } from "react-router-dom";

const NAV_REM = "3.5rem";

/**
 * Owns the clipboard / repo inspection state shared between the persistent
 * left sidebar (ClipboardSidebarSections, mounted by AppShell) and the routed
 * content + right preview panel (TreeViewShell). It must sit above both, so
 * AppShell mounts it; TreeViewShell only renders from it.
 */
export const TreeViewProvider = ({ children }: { children: ReactNode }) => {
  const repoMatch = useMatch({ path: "/repos/:repoId", end: false });
  const commitsRepoId = repoMatch?.params.repoId ?? null;
  const location = useLocation();

  const [selectedPaper, setSelectedPaper] = useState<RepoPaperRow | null>(null);
  const [clipboardPreview, setClipboardPreview] = useState<DocumentContent | null>(null);
  const [clipboardPreviewLoading, setClipboardPreviewLoading] = useState(false);
  const [clipboardActions, setClipboardActions] = useState<ClipboardActions | null>(null);
  const [inspectedDoc, setInspectedDoc] = useState<InspectedDoc>(null);
  const closeLeftSidebar = useSetAtom(closeLeftSidebarAtom);
  const [leftSidebarOpen] = useAtom(leftSidebarOpenAtom);

  const breakpoints = useBreakpoints({
    troubleSpot: (width) => width >= 1024 && width < 1350,
  });

  useEffect(() => {
    setSelectedPaper(null);
  }, [commitsRepoId]);

  useEffect(() => {
    closeLeftSidebar();
  }, [location.pathname, closeLeftSidebar]);

  const wrappedSetSelectedPaper: typeof setSelectedPaper = useCallback(
    (action) => {
      setSelectedPaper((prev) => {
        const next = typeof action === "function" ? action(prev) : action;
        if (next) {
          setClipboardPreview(null);
          setClipboardPreviewLoading(false);
        }
        return next;
      });
    },
    [],
  );

  const wrappedSetClipboardPreview: typeof setClipboardPreview = useCallback(
    (action) => {
      setClipboardPreview((prev) => {
        const next = typeof action === "function" ? action(prev) : action;
        if (next) setSelectedPaper(null);
        return next;
      });
    },
    [],
  );

  const wrappedSetClipboardPreviewLoading: typeof setClipboardPreviewLoading =
    useCallback((action) => {
      setClipboardPreviewLoading((prev) => {
        const next = typeof action === "function" ? action(prev) : action;
        if (next) setSelectedPaper(null);
        return next;
      });
    }, []);

  const panelHasContent = !!selectedPaper || !!clipboardPreview || clipboardPreviewLoading;
  const [detailPanelOpen, setDetailPanelOpen] = useState(false);
  const detailCloseRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const [panelWidth, setPanelWidth] = useState(breakpoints.troubleSpot ? 320 : 384);

  useEffect(() => {
    if (panelHasContent) {
      clearTimeout(detailCloseRef.current);
      setDetailPanelOpen(true);
      closeLeftSidebar();
    }
  }, [panelHasContent, closeLeftSidebar]);

  useEffect(() => {
    return () => clearTimeout(detailCloseRef.current);
  }, []);

  const closeDetailPanel = useCallback(() => {
    setDetailPanelOpen(false);
    detailCloseRef.current = setTimeout(() => {
      setSelectedPaper(null);
      setClipboardPreview(null);
      setClipboardPreviewLoading(false);
    }, 300);
  }, []);

  const resetPanels = useCallback(() => {
    clearTimeout(detailCloseRef.current);
    setDetailPanelOpen(false);
    setSelectedPaper(null);
    setClipboardPreview(null);
    setClipboardPreviewLoading(false);
    setInspectedDoc(null);
  }, []);

  useEffect(() => {
    if (leftSidebarOpen) {
      closeDetailPanel();
    }
  }, [leftSidebarOpen, closeDetailPanel]);

  useEffect(() => {
    const lock =
      typeof document !== "undefined" &&
      (leftSidebarOpen || panelHasContent) &&
      window.matchMedia("(max-width: 1023px)").matches;

    if (lock) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
    return undefined;
  }, [leftSidebarOpen, panelHasContent]);

  const showClipboardPanel = !!clipboardPreview || clipboardPreviewLoading;

  const value = useMemo(
    (): TreeViewOutletContextValue => ({
      selectedPaper,
      setSelectedPaper: wrappedSetSelectedPaper,
      commitsRepoId,
      closeMobileTreeView: closeLeftSidebar,
      clipboardPreview,
      setClipboardPreview: wrappedSetClipboardPreview,
      clipboardPreviewLoading,
      setClipboardPreviewLoading: wrappedSetClipboardPreviewLoading,
      clipboardPreviewOpen: detailPanelOpen && showClipboardPanel,
      clipboardActions,
      setClipboardActions,
      inspectedDoc,
      setInspectedDoc,
      detailPanelOpen,
      panelWidth,
      setPanelWidth,
      closeDetailPanel,
      resetPanels,
    }),
    [
      commitsRepoId,
      selectedPaper,
      closeLeftSidebar,
      wrappedSetSelectedPaper,
      clipboardPreview,
      wrappedSetClipboardPreview,
      clipboardPreviewLoading,
      wrappedSetClipboardPreviewLoading,
      detailPanelOpen,
      showClipboardPanel,
      clipboardActions,
      inspectedDoc,
      panelWidth,
      closeDetailPanel,
      resetPanels,
    ],
  );

  return (
    <TreeViewOutletContext.Provider value={value}>{children}</TreeViewOutletContext.Provider>
  );
};

/**
 * Nested layout for clipboard / repo routes: the routed content plus the
 * right-hand preview panel. State lives in TreeViewProvider (mounted by
 * AppShell) so the left sidebar can read the same selection.
 */
export const TreeViewShell = () => {
  const {
    clipboardPreview,
    clipboardPreviewLoading,
    detailPanelOpen,
    panelWidth,
    setPanelWidth,
    closeDetailPanel,
    resetPanels,
  } = useTreeViewOutletContext();
  const showClipboardPanel = !!clipboardPreview || clipboardPreviewLoading;

  // Leaving the shell (e.g. to /chat) drops whatever the panel was showing.
  useEffect(() => resetPanels, [resetPanels]);

  // AppShell owns NavBar + left rail; this nested layout adds the right preview panel.
  return (
    <div className="relative flex h-full min-h-0 flex-1 overflow-hidden bg-zinc-50">
      <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">
        <div className="min-h-0 min-w-0 flex-1 overflow-auto">
          <Outlet />
        </div>
      </div>
      <SlideablePanel
        isOpen={detailPanelOpen}
        onClose={closeDetailPanel}
        offsetTop={NAV_REM}
        from="right"
        width={`${panelWidth}px`}
        alwaysOpenOnDesktop={false}
        ariaLabel={showClipboardPanel ? "Clipboard preview" : "Paper details"}
        resizability={{ onWidthChange: setPanelWidth, minWidth: 280 }}
      >
        {showClipboardPanel ? (
          <ClipboardPreviewPanel onClose={closeDetailPanel} />
        ) : (
          <PaperDetailPanel onClose={closeDetailPanel} />
        )}
      </SlideablePanel>
    </div>
  );
};
